import { useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';

import { useFieldWiring } from '@nesso/ui/hooks/use-field-wiring';
import { cn } from '@nesso/ui/lib/utils';
import { Button } from '@nesso/ui/primitives/button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandItem,
    CommandList,
} from '@nesso/ui/primitives/command';
import { Field, FieldLabel, FieldSlot } from '@nesso/ui/primitives/field';
import { inputInvalidClassName } from '@nesso/ui/primitives/input';
import { Popover, PopoverContent, PopoverTrigger } from '@nesso/ui/primitives/popover';
import { selectTriggerClassName } from '@nesso/ui/primitives/select';

import { getFilteredServiceOptions, serviceCatalogOptions } from '@/entities/service-catalog';

import { getServiceCodeOptions } from '../../../../lib/get-service-code-options';
import { SaveServiceCodePickerSearchInput } from './save-service-code-picker/save-service-code-picker-search-input';

type Props = {
    value: string;
    error?: boolean | string;
    disabled?: boolean;
    onSelect: (code: string, description: string) => void;
};

// Popover + Command, not Combobox: the design draws a separate search panel under a select-style
// trigger (Figma 301:6623), and the picker needs disabled options and a create action that
// `Combobox` has no slot for.
export function SaveServiceCodePicker({ value, error, disabled, onSelect }: Props) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const { data: catalog } = useQuery(serviceCatalogOptions());
    const wiring = useFieldWiring({ error });

    const options = useMemo(
        () => getServiceCodeOptions(catalog ?? { services: [], customCodes: [] }),
        [catalog],
    );

    const trimmedSearch = search.trim();
    const filteredOptions = getFilteredServiceOptions(options, trimmedSearch);

    function selectOption(code: string, description: string) {
        onSelect(code, description);
        setSearch('');
        setOpen(false);
    }

    return (
        // Figma 624:8286 / 631:20537: 137px at every width, not full width — 136px (`w-34`) is
        // the nearest scale value.
        <Field data-invalid={wiring.dataInvalid} className="w-34">
            <FieldLabel htmlFor={wiring.controlId}>Code</FieldLabel>
            {/* modal: gives the portaled list its own scroll lock; the sheet's lock blocks wheel and touch. */}
            <Popover open={open} onOpenChange={setOpen} modal={true}>
                <PopoverTrigger
                    {...wiring.controlProps}
                    type="button"
                    disabled={disabled}
                    className={cn(
                        selectTriggerClassName,
                        inputInvalidClassName,
                        'bg-input-background justify-between',
                    )}
                >
                    <span>{value}</span>
                    <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                </PopoverTrigger>
                <PopoverContent
                    // Figma 301:6623: 377px (`w-94` = 376px) regardless of the trigger's own
                    // width; `max-w-(--radix-popover-content-available-width)` plus
                    // `collisionPadding` matching the sheet's own padding keep it inside the
                    // 385px phone sheet (Figma 629:10293) instead of overflowing past it.
                    className="w-94 max-w-(--radix-popover-content-available-width) p-0"
                    align="start"
                    collisionPadding={24}
                >
                    {/* cmdk's own re-sort re-appends only non-disabled matches, leaving a disabled
                    match (an already-added code) behind at the front; `shouldFilter={false}` skips
                    it and `getFilteredServiceOptions` filters below, in code order instead. */}
                    <Command shouldFilter={false}>
                        <SaveServiceCodePickerSearchInput
                            value={search}
                            onValueChange={setSearch}
                            onCreateCustomCode={() => selectOption(trimmedSearch, '')}
                        />
                        <CommandList>
                            <CommandEmpty>
                                {trimmedSearch === '' ? (
                                    'Code not found.'
                                ) : (
                                    <>
                                        Code not found.{' '}
                                        <Button
                                            type="button"
                                            variant="link"
                                            className="h-auto p-0 text-sm text-popover-foreground underline underline-offset-auto cursor-pointer font-normal align-baseline"
                                            onClick={() => selectOption(trimmedSearch, '')}
                                        >
                                            Add custom code {trimmedSearch}
                                        </Button>
                                    </>
                                )}
                            </CommandEmpty>
                            {filteredOptions.length > 0 && (
                                <CommandGroup>
                                    {filteredOptions.map((option) => (
                                        <CommandItem
                                            key={option.code}
                                            value={option.code}
                                            disabled={option.added}
                                            onSelect={() =>
                                                selectOption(option.code, option.description)
                                            }
                                        >
                                            {/* Figma 301:6623 truncates the whole line, "(added)" included. */}
                                            <span className="truncate">
                                                {option.label}
                                                {option.added && ' (added)'}
                                            </span>
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                            )}
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>
            <FieldSlot errorMessage={wiring.errorMessage} errorId={wiring.errorId} />
        </Field>
    );
}
